import { monitorForElements } from '@atlaskit/pragmatic-drag-and-drop/element/adapter'
import { AnimatePresence } from 'motion/react'
import { usePostHog } from 'posthog-js/react'
import { type ReactElement, useEffect } from 'react'
import { useOrderApplicantStatusMutation } from '~/api/mutations/order'
import type { ApplicantHeadData } from '~/api/queries/applicant'
import type { OrderListFilters } from '~/api/queries/order'
import { KanbanColumn } from '~/components/Global/Blocks/Kanban/KanbanColumn'
import { AcceptModal } from '~/components/Scopes/Orders/Modals/AcceptModal'
import { acceptModal } from '~/components/Scopes/Orders/Modals/modals'
import { ApplicantCardShimmer } from '~/components/Scopes/Orders/OrderKanban/ApplicantCardShimmer'
import {
  columnApplicants,
  wrapper
} from '~/components/Scopes/Orders/OrderKanban/OrderKanban.css'
import { useTaxonomy } from '~/domains/taxonomies/queries/useTaxonomy'
import { captureOrderAction } from '../analytics'
import { GlobalKanbanColumn } from './GlobalKanbanColumn'

type GlobalKanbanProps = {
  orderFilters: OrderListFilters
}

export const GlobalKanban = ({
  orderFilters
}: GlobalKanbanProps): ReactElement => {
  const posthog = usePostHog()
  const { data: orderStatuses, isLoading } = useTaxonomy(
    'applicant_order_statuses'
  )
  const { mutate: updateApplicantStatus } = useOrderApplicantStatusMutation()
  const acceptedStatusId = orderStatuses?.find(
    (status) => status.specialStatus === 'accepted'
  )?.id

  useEffect(() => {
    return monitorForElements({
      onDrop: ({ source, location }) => {
        const dropTarget = location.current.dropTargets?.[0]
        if (!dropTarget) return

        const toStatusId = dropTarget.data.columnId as number | undefined
        const dragData = source.data as
          | (ApplicantHeadData & { orderId?: number })
          | undefined

        if (!toStatusId || !dragData?.orderId) return

        const applicant: ApplicantHeadData = dragData
        const orderId: number = dragData.orderId

        captureOrderAction(posthog, 'status_change', {
          kanban: 'global',
          applicantId: applicant.id,
          orderId,
          toStatusId
        })

        if (acceptedStatusId === toStatusId) {
          void acceptModal(
            {
              id: orderId,
              applicantWantedCount: 0,
              applicantAcceptedCount: 0,
              contractTerm: null
            },
            [applicant],
            toStatusId
          )
          return
        }

        updateApplicantStatus({
          applicants: [applicant],
          status: toStatusId,
          orderId,
          notifyProgress: true
        })
      }
    })
  }, [updateApplicantStatus, posthog, acceptedStatusId])

  if (isLoading || !orderStatuses) {
    return (
      <div className={wrapper}>
        {[...Array(5)].map((_, i) => (
          // biome-ignore lint/suspicious/noArrayIndexKey: shimmer
          <KanbanColumn title="Chargement..." key={i}>
            <div className={columnApplicants}>
              {[...Array(5)].map((_, j) => (
                // biome-ignore lint/suspicious/noArrayIndexKey: shimmer
                <ApplicantCardShimmer key={j} />
              ))}
            </div>
          </KanbanColumn>
        ))}
      </div>
    )
  }

  return (
    <div className={wrapper}>
      <AnimatePresence>
        {orderStatuses.map((status) => (
          <GlobalKanbanColumn
            key={`global_status_${status.id}`}
            status={status}
            orderFilters={orderFilters}
          />
        ))}
      </AnimatePresence>
      <AcceptModal />
    </div>
  )
}
